import { clsx } from '@mentimeter/ragnar-tailwind-config';
import React from 'react';
import { Grid } from '@mentimeter/slides-presentation-ui/grid';
import { MasonryGrid } from '@mentimeter/slides-presentation-ui/masonry-grid';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Heading } from '@mentimeter/ragnar-ui/typography';
import type { TextResponse } from '@core-api/presentation/types/response';
import { useTranslations } from 'next-intl';
import { Response } from '../../Response';
import { getVotingOnAnswersSplitResults } from '../../utils/getVotingOnAnswersSplitResults';
import { ResponseWithUpvotes } from '../../ResponseWithUpvotes';
import { ModerationButton } from '../../ui/ModerationButton';
import { NUM_COLUMNS_WITHOUT_IMAGE_LAYOUT } from '../../utils/constants';

interface ResponsesGridProps {
  responses: TextResponse[];
  shouldSplitResponses: boolean;
  numberOfColumns?: number;
  onItemClick: (index: number) => void;
  isResponsesHidden: boolean;
  shouldAnimate: boolean;
  isReadonly: boolean;
}

export const ResponsesGrid = ({
  responses,
  shouldSplitResponses,
  numberOfColumns = NUM_COLUMNS_WITHOUT_IMAGE_LAYOUT,
  onItemClick,
  isResponsesHidden,
  shouldAnimate,
  isReadonly,
}: ResponsesGridProps) => {
  const translate = useTranslations('presenting.legacy');

  if (!shouldSplitResponses) {
    return (
      <MasonryGrid
        numberOfColumns={numberOfColumns}
        shouldAnimate={shouldAnimate}
      >
        {responses.map((item, index) => (
          <Response
            isTextHidden={isResponsesHidden}
            index={index}
            key={item.id}
            responseText={item.responseText}
            participantDisplayName={item.participant?.displayName ?? null}
            onClick={onItemClick}
            controlsOnHover={
              <ModerationButton selectedAnswer={item.responseText} />
            }
            shouldAnimate={shouldAnimate}
            isReadonly={isReadonly}
          />
        ))}
      </MasonryGrid>
    );
  }

  const {
    resultsWithVotes,
    resultsWithoutVotes,
    indexOfFirstResultWithoutVotes,
  } = getVotingOnAnswersSplitResults(responses);

  const maxVoteCount = Math.max(...resultsWithVotes.map((v) => v.voteCount));
  const hasResultsWithVotes = resultsWithVotes.length > 0;

  return (
    <>
      {hasResultsWithVotes && (
        <Box
          as="section"
          data-testid="responses--voted"
          flexDirection="column"
          alignContent="flex-start"
          flexWrap="wrap"
          width="100%"
          mb={5}
        >
          <Grid numberOfColumns={numberOfColumns} shouldAnimate={shouldAnimate}>
            {resultsWithVotes.map((item, index) => (
              <ResponseWithUpvotes
                key={item.id}
                index={index}
                responseText={item.responseText}
                participantDisplayName={item.participant?.displayName ?? null}
                upvotes={item.voteCount}
                onClick={onItemClick}
                isTopVoted={item.voteCount === maxVoteCount}
                controlsOnHover={
                  <ModerationButton selectedAnswer={item.responseText} />
                }
                isTextHidden={isResponsesHidden}
                isReadonly={isReadonly}
              />
            ))}
          </Grid>
        </Box>
      )}
      {resultsWithoutVotes.length > 0 && (
        <Box
          as="section"
          data-testid="responses--unvoted"
          flexDirection="column"
          alignContent="flex-start"
          flexWrap="wrap"
          width="100%"
        >
          {hasResultsWithVotes && (
            <Heading
              className={clsx(
                'text-150',
                'font-normal',
                'font-heading',
                'mb-4',
                'text-text-weaker',
                'w-full',
              )}
            >
              {translate('open.responses_without_votes')}
            </Heading>
          )}

          <MasonryGrid
            numberOfColumns={numberOfColumns}
            shouldAnimate={shouldAnimate}
            initialDelay={hasResultsWithVotes ? 700 : 0}
          >
            {resultsWithoutVotes.map((item, index) => (
              <Response
                index={index + indexOfFirstResultWithoutVotes} // Index of elements should continue from the previous grid, important for the onClick handler
                key={item.id}
                responseText={item.responseText}
                participantDisplayName={item.participant?.displayName ?? null}
                onClick={onItemClick}
                isTextHidden={isResponsesHidden}
                controlsOnHover={
                  <ModerationButton selectedAnswer={item.responseText} />
                }
                shouldAnimate={shouldAnimate}
                isReadonly={isReadonly}
              />
            ))}
          </MasonryGrid>
        </Box>
      )}
    </>
  );
};
